import React, {useState, useEffect, useCallback, useRef} from 'react';
import {Field, Form} from 'react-final-form';
import t from 'babel-plugin-yate/macro';
import isEmpty from 'lodash-es/isEmpty';
import {connect} from 'react-redux';
import {
  composeFormValidators,
  isPhoneNumber,
  isRequired,
  isNumber,
} from '../../lib/validations';
import {
  formatWhiteSpaceFormField,
  parseOnlyNumericFormField,
  openBase64File,
} from '../../lib/misc';
import {
  esignInit,
  esignAuthCode,
  esignResendAuthCode,
  esignPostAuth,
} from '../../store/request';
import {Input} from '../../components/Form/Input';
import Button from '../../components/Button';
import {DocPreview, DocRow} from '../DocPreview/previewHelpers';
import ValidationError from '../../components/Form/ValidationError';

let codeTm;
let pauseTm;
let funcTm;
let resetTm;

const fileNameByStatus = {
  awaiting_anaf_consent: t`Acord ANAF`,
  awaiting_esign: 'Credit contract',
};

function Esign(props) {
  const {
    uuid,
    setBtnDisabled,
    esignInit,
    esignAuthCode,
    esignResendAuthCode,
    esignPostAuth,
    setIframeMethodType,
    status,
  } = props;

  const formRef = useRef(null);
  const [countCode, setCountCode] = useState(0);
  const [disableInput, setDisableInput] = useState(true);
  const [esignData, setEsignData] = useState(null);
  const [esignDataMessages, setEsignDataMessages] = useState(null);
  const [pause, setPause] = useState(false);
  const [errorEsignCode, setErrorEsignCode] = useState(null);

  const onErrorHandler = useCallback(
    (res, callback) => {
      setEsignDataMessages(res);
      setEsignData(null);
      setBtnDisabled(false);

      clearInterval(funcTm);
      funcTm = setInterval(callback, 10 * 1000);
    },
    [setBtnDisabled],
  );

  const initCert = useCallback(async () => {
    const res = await esignInit(uuid);

    if (res?.messages?.code === 202) {
      setEsignDataMessages(res);
      return;
    }

    if (isEmpty(res?.data)) {
      onErrorHandler(res, initCert);
      return;
    }

    clearInterval(funcTm);
    setEsignData(res.data);

    // reset state
    setCountCode(0);
    setEsignDataMessages(null);
    setErrorEsignCode(null);
    setPause(false);
    setDisableInput(true);
  }, [esignInit, onErrorHandler, uuid]);

  const onSuccessCode = useCallback(() => {
    clearTimeout(codeTm);
    clearTimeout(pauseTm);
    clearTimeout(resetTm);

    setCountCode(count => ++count);
    setPause(true);
    setDisableInput(false);

    codeTm = setTimeout(
      () => {
        setDisableInput(true);
        setErrorEsignCode(null);
        formRef.current.change('smsCode', undefined);
        formRef.current.resetFieldState('smsCode');
      },
      5 * 60 * 1000,
    );

    pauseTm = setTimeout(() => {
      setPause(false);
    }, 60 * 1000);

    resetTm = setTimeout(
      () => {
        setCountCode(3);
      },
      10 * 60 * 1000,
    );
  }, []);

  const authCode = useCallback(async () => {
    if (countCode === 3) {
      await initCert();
    }

    const fetchCode =
      countCode === 0 || countCode === 3 ? esignAuthCode : esignResendAuthCode;
    const res = await fetchCode(uuid);

    if (isEmpty(res.data)) {
      setEsignDataMessages(res);
      return;
    }

    onSuccessCode();
  }, [
    uuid,
    esignAuthCode,
    esignResendAuthCode,
    onSuccessCode,
    countCode,
    initCert,
  ]);

  const postAuth = useCallback(
    async code => {
      const res = await esignPostAuth(uuid, code);

      if (res.status === 'success') {
        if (isEmpty(res.data)) {
          onErrorHandler(res, initCert);
          return;
        }

        setBtnDisabled(false);
        setEsignDataMessages(res);
        setErrorEsignCode(null);
      } else {
        setErrorEsignCode(res);
      }
    },
    [uuid, esignPostAuth, onErrorHandler, initCert, setBtnDisabled],
  );

  useEffect(() => {
    initCert();
    setIframeMethodType('ESign');

    return () => {
      clearTimeout(codeTm);
      clearTimeout(pauseTm);
      clearInterval(funcTm);
      clearInterval(resetTm);
    };
  }, [initCert, setIframeMethodType]);

  if (!esignData && !esignDataMessages) {
    return [];
  }

  if (esignDataMessages) {
    return (
      <div className="relative w-full">
        <div className="h-24 flex items-center justify-center text-36">
          <div className="h-16 spinner" />
        </div>

        <div className="py-4 px-8 text-center">
          {esignDataMessages?.messages?.content}
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full">
      <DocPreview />

      <DocRow
        fileName={fileNameByStatus[status]}
        url={openBase64File(esignData.documents[0].data)}
        isDefault
      />

      <DocRow
        fileName={t`Terms and conditions`}
        url={openBase64File(esignData.terms_and_conditions_response.file)}
      />

      <Form
        onSubmit={() => {}}
        render={({values, form}) => {
          formRef.current = form;

          return (
            <div className="p-4">
              <div className="md:flex">
                <div className="pb-4 md:w-2/3">
                  <Field
                    name="customerMobilePhone"
                    label={t`Mobile number`}
                    component={Input}
                    disabled={true}
                    initialValue={esignData.customer_mobile_phone}
                    type="number"
                    validate={composeFormValidators(
                      isRequired(),
                      isPhoneNumber(),
                    )}
                  />

                  <div
                    className="pt-2"
                    title={
                      pause ? 'Puteti trimite un nou SMS peste 1 min' : null
                    }>
                    <Button
                      title={t`Genereaza cod (SMS) si confirma termeni & conditii`}
                      customProps={{type: 'button', disabled: pause}}
                      onClick={authCode}
                      color="blue"
                      border={false}
                    />
                  </div>
                </div>

                <div
                  className="pb-4 md:pl-4 md:w-1/3"
                  {...(disableInput
                    ? {
                        title: t`Mai intai validati codul pentru autorizarea semnaturii`,
                      }
                    : {})}>
                  <Field
                    autoComplete="one-time-code"
                    label={t`Introduceti codul primit prin SMS`}
                    name="smsCode"
                    component={Input}
                    disabled={disableInput}
                    parse={parseOnlyNumericFormField}
                    format={formatWhiteSpaceFormField}
                    validate={composeFormValidators(isRequired(), isNumber())}
                  />

                  {errorEsignCode?.error && (
                    <ValidationError value={errorEsignCode.errorMessage} />
                  )}

                  <div className="pt-2">
                    <Button
                      title={t`Sign`}
                      customProps={{
                        type: 'button',
                        disabled: disableInput || !values.smsCode,
                      }}
                      onClick={() => postAuth(values.smsCode)}
                      color="green"
                      border={false}
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        }}
      />
    </div>
  );
}

export default connect(null, {
  esignInit,
  esignAuthCode,
  esignResendAuthCode,
  esignPostAuth,
})(Esign);
